import { Blockquote, Button } from '@ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useApi } from '../../../../hooks/useApi';
import { useModal } from '../../../../hooks/useModal';
import { useClubSlug } from '../../../Clubs/hooks';
import Loader from '../../../Loader';
import GroupAssignmentForm from '../../Group/GroupAssignmentForm';
import GuessCard from './GuessCard';
import { Guess } from './types';

interface Props {
    memberId: string;
    callback?: () => void;
}

const GroupGuesser = ({ memberId, callback }: Props) => {
    const { t } = useTranslation('member-management');
    const api = useApi();
    const clubSlug = useClubSlug();
    const [setModal] = useModal();
    const [isLoading, setIsLoading] = useState(false);
    const { data, isLoading: isLoadingGuesses } = useSWR<{
        data: Guess[];
    }>(memberId ? `club-member/${memberId}/guess` : null);

    const handleShowGroupAssignmentForm = useCallback(() => {
        setModal({
            title: t('groups.guesser.assignTitle'),
            content: <GroupAssignmentForm clubSlug={clubSlug} selection={[memberId]} callback={callback} />,
            show: true
        });
    }, [clubSlug, memberId, callback, t]);

    const handleSelectGuess = useCallback(
        (guess: Guess) => {
            setIsLoading(true);
            return api({
                url: `club-member/${clubSlug}/group/${guess.group.id}`,
                method: 'PUT',
                data: { members: [memberId] },
                onBefore: () => setIsLoading(false),
                onSuccess: () => callback?.()
            });
        },
        [clubSlug, memberId, callback]
    );

    return (
        <Loader isLoading={isLoadingGuesses}>
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                {data?.data?.length > 0 ? (
                    data?.data?.map((guess) => (
                        <GuessCard onClick={() => handleSelectGuess(guess)} guess={guess} key={guess.group.id} />
                    ))
                ) : (
                    <Blockquote className="tw-col-span-2 tw-mb-4">
                        {t('groups.guesser.noGuesses')}
                    </Blockquote>
                )}
            </div>
            <Button onClick={handleShowGroupAssignmentForm} color="warning" block auto>
                {t('groups.guesser.assignManually')}
            </Button>
        </Loader>
    );
};

export default GroupGuesser;
